import { useState, useCallback, useEffect } from 'react';
import { Link2, Mail, Phone, MessageSquare, Wifi, Type } from 'lucide-react';
import { useQR } from '@/hooks/useQRContext';
import { useDebounce } from '@/hooks/useDebounce';
import { validateQRData, detectDataType } from '@/utils/validators';
import Input from '@/components/common/Input';
import Textarea from '@/components/common/Textarea';
import { cn } from '@/utils/helpers';

/**
 * Data type configurations for different input modes
 */
const DATA_TYPES = [
  { id: 'url', label: 'URL', icon: Link2 },
  { id: 'text', label: 'Text', icon: Type },
  { id: 'email', label: 'Email', icon: Mail },
  { id: 'phone', label: 'Phone', icon: Phone },
  { id: 'sms', label: 'SMS', icon: MessageSquare },
  { id: 'wifi', label: 'WiFi', icon: Wifi },
];

/**
 * QR Input component for entering content to encode
 */
function QRInput() {
  const { qrConfig, updateConfig } = useQR();
  const [inputType, setInputType] = useState('url');
  const [localValue, setLocalValue] = useState(qrConfig.data || '');
  
  // WiFi specific state
  const [wifiSSID, setWifiSSID] = useState('');
  const [wifiPassword, setWifiPassword] = useState('');
  const [wifiEncryption, setWifiEncryption] = useState('WPA');

  // Debounce the config update for performance
  const debouncedUpdate = useDebounce(localValue, 300);

  // Update QR config when debounced value changes
  useEffect(() => {
    if (debouncedUpdate !== qrConfig.data) {
      updateConfig('data', debouncedUpdate);
    }
  }, [debouncedUpdate, qrConfig.data, updateConfig]);

  // Handle input change
  const handleInputChange = useCallback((value) => {
    setLocalValue(value);
    
    // Format value based on input type
    let formattedValue = value;
    
    switch (inputType) {
      case 'email':
        if (value && !value.startsWith('mailto:')) {
          formattedValue = `mailto:${value}`;
        }
        break;
      case 'phone':
        if (value && !value.startsWith('tel:')) {
          formattedValue = `tel:${value}`;
        }
        break;
      case 'sms':
        if (value && !value.startsWith('sms:')) {
          formattedValue = `sms:${value}`;
        }
        break;
      default:
        formattedValue = value;
    }
    
    updateConfig('data', formattedValue);
  }, [inputType, updateConfig]);

  // Handle WiFi configuration
  const handleWifiChange = useCallback(() => {
    if (wifiSSID) {
      const wifiString = `WIFI:T:${wifiEncryption};S:${wifiSSID};P:${wifiPassword};;`;
      setLocalValue(wifiString);
      updateConfig('data', wifiString);
    }
  }, [wifiSSID, wifiPassword, wifiEncryption, updateConfig]);

  // Validate current input
  const validation = validateQRData(localValue);
  const detectedType = detectDataType(localValue);

  // Get placeholder based on input type
  const getPlaceholder = () => {
    switch (inputType) {
      case 'url': return 'https://example.com';
      case 'email': return 'hello@example.com';
      case 'phone': return '+1 (555) 123-4567';
      case 'sms': return '+1 (555) 123-4567';
      case 'wifi': return '';
      case 'text': return 'Enter any text...';
      default: return 'Enter content...';
    }
  };

  // Render WiFi-specific inputs
  const renderWiFiInputs = () => (
    <div className="space-y-4">
      <Input
        label="Network Name (SSID)"
        value={wifiSSID}
        onChange={(e) => {
          setWifiSSID(e.target.value);
          setTimeout(handleWifiChange, 100);
        }}
        placeholder="My WiFi Network"
      />
      <Input
        type="password"
        label="Password"
        value={wifiPassword}
        onChange={(e) => {
          setWifiPassword(e.target.value);
          setTimeout(handleWifiChange, 100);
        }}
        placeholder="WiFi password"
      />
      <div className="space-y-1.5">
        <label className="block text-sm font-medium text-gray-700">
          Encryption
        </label>
        <div className="flex gap-2">
          {['WPA', 'WEP', 'nopass'].map((enc) => (
            <button
              key={enc}
              onClick={() => {
                setWifiEncryption(enc);
                setTimeout(handleWifiChange, 100);
              }}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-medium transition-all',
                wifiEncryption === enc
                  ? 'bg-primary-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              )}
            >
              {enc === 'nopass' ? 'None' : enc}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Input Type Selector */}
      <div className="flex flex-wrap gap-2">
        {DATA_TYPES.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setInputType(id)}
            className={cn(
              'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all',
              inputType === id
                ? 'bg-primary-500 text-white shadow-md shadow-primary-500/25'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            )}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Main Input */}
      {inputType === 'wifi' ? (
        renderWiFiInputs()
      ) : inputType === 'text' ? (
        <Textarea
          label="Text Content"
          value={localValue}
          onChange={(e) => handleInputChange(e.target.value)}
          placeholder={getPlaceholder()}
          rows={4}
          hint={`${localValue.length} characters`}
        />
      ) : (
        <Input
          label={DATA_TYPES.find(t => t.id === inputType)?.label || 'Content'}
          value={localValue.replace(/^(mailto:|tel:|sms:)/, '')}
          onChange={(e) => handleInputChange(e.target.value)}
          placeholder={getPlaceholder()}
          icon={DATA_TYPES.find(t => t.id === inputType)?.icon}
        />
      )}

      {/* Validation Status */}
      {localValue && (
        <div className={cn(
          'flex items-center gap-2 text-sm p-3 rounded-lg',
          validation.valid ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
        )}>
          {validation.valid ? (
            <>
              <span className="font-medium">Valid {validation.type}</span>
              {detectedType !== inputType && inputType !== 'text' && (
                <span className="text-gray-500">
                  (detected as {detectedType})
                </span>
              )}
            </>
          ) : (
            <span>{validation.message}</span>
          )}
        </div>
      )}
    </div>
  );
}

export default QRInput;
